/**
 * Unified model loading facade that auto-detects format by file extension
 * and delegates to the appropriate sub-pipeline.
 *
 * Canonical home for ModelFormat, ModelResult, and LoadOptions types.
 */

import type * as THREE from 'three/webgpu';
import { GLTFPipeline } from './GLTFPipeline.js';
import type { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { OBJPipeline } from './OBJPipeline.js';
import { FBXPipeline } from './FBXPipeline.js';
import { USDZPipeline } from './USDZPipeline.js';
import { Logger } from '../core/Logger.js';
// Type-only (erased at runtime) — importing the MeshOps VALUE eagerly would pull
// in three-bvh-csg (→ three-mesh-bvh), which fails to load in a Node test env.
import type { SmoothOptions } from './MeshOps.js';

/* ------------------------------------------------------------------ */
/*  Canonical shared types                                             */
/* ------------------------------------------------------------------ */

export type ModelFormat = 'gltf' | 'obj' | 'fbx' | 'usdz';

export interface ModelResult {
  scene: THREE.Group;
  scenes: THREE.Group[];
  cameras: THREE.Camera[];
  animations: THREE.AnimationClip[];
  format: ModelFormat;
  meta: Record<string, unknown>;
}

export interface LoadOptions {
  onProgress?: (event: ProgressEvent) => void;
  mtlUrl?: string;
  texturePath?: string;
  /** When set, run MeshOps.smoothModel on the loaded scene (gltf only). Default: off. */
  smoothing?: SmoothOptions;
}

export type { SmoothOptions } from './MeshOps.js';

/* ------------------------------------------------------------------ */
/*  Extension → format mapping                                         */
/* ------------------------------------------------------------------ */

const EXTENSION_MAP: Record<string, ModelFormat> = {
  '.gltf': 'gltf',
  '.glb': 'gltf',
  '.obj': 'obj',
  '.fbx': 'fbx',
  '.usdz': 'usdz',
};

const SUPPORTED_EXTENSIONS = Object.keys(EXTENSION_MAP).map((e) => `"${e}"`).join(', ');

/* ------------------------------------------------------------------ */
/*  ModelPipeline                                                      */
/* ------------------------------------------------------------------ */

export class ModelPipeline {
  private gltf: GLTFPipeline;
  private obj: OBJPipeline;
  private fbx: FBXPipeline;
  private usdz: USDZPipeline;

  constructor() {
    this.gltf = new GLTFPipeline();
    this.obj = new OBJPipeline();
    this.fbx = new FBXPipeline();
    this.usdz = new USDZPipeline();
  }

  /**
   * Initialize the GLTF sub-pipeline. Must be called after renderer.init().
   */
  /** The KTX2 transcoder (lend it to a second pipeline instead of building another worker pool). */
  get ktx2(): KTX2Loader | null { return this.gltf.ktx2; }

  init(renderer: THREE.WebGPURenderer, opts: { ktx2Loader?: KTX2Loader | null } = {}): void {
    if (opts.ktx2Loader) this.gltf.init(renderer, opts.ktx2Loader);
    else this.gltf.init(renderer); // the one-arg form stays the contract (and the unit test's expectation)
    Logger.info('Assets', 'ModelPipeline initialized');
  }

  /**
   * Load a model, auto-detecting format from the file extension.
   *
   * @param url - URL of the model file
   * @param options - Optional load settings
   */
  async load(url: string, options?: LoadOptions): Promise<ModelResult> {
    const format = this.detectFormat(url);
    return this.loadAs(url, format, options);
  }

  /**
   * Load a model with an explicitly specified format, bypassing
   * extension-based detection.
   *
   * @param url - URL of the model file
   * @param format - Model format to use
   * @param options - Optional load settings
   */
  async loadAs(url: string, format: ModelFormat, options?: LoadOptions): Promise<ModelResult> {
    switch (format) {
      case 'gltf': {
        const gltf = await this.gltf.load(url, options?.onProgress);
        if (options?.smoothing) {
          // Lazy: load MeshOps (and its three-bvh-csg dep) only when smoothing is
          // actually requested, so merely importing ModelPipeline stays dep-light.
          const { MeshOps } = await import('./MeshOps.js');
          await MeshOps.smoothModel(gltf.scene, options.smoothing);
        }
        return {
          scene: gltf.scene,
          scenes: gltf.scenes,
          cameras: gltf.cameras,
          animations: gltf.animations,
          format: 'gltf' as const,
          meta: gltf.asset as Record<string, unknown>,
        };
      }
      case 'obj':
        return this.obj.load(url, options);
      case 'fbx':
        return this.fbx.load(url, options);
      case 'usdz':
        return this.usdz.load(url, options);
    }
  }

  /**
   * Dispose all sub-pipelines and release resources.
   */
  dispose(): void {
    this.gltf.dispose();
    this.obj.dispose();
    (this.fbx as any).dispose?.();
    (this.usdz as any).dispose?.();
    Logger.debug('Assets', 'ModelPipeline disposed');
  }

  /* ---------------------------------------------------------------- */
  /*  Private helpers                                                  */
  /* ---------------------------------------------------------------- */

  private detectFormat(url: string): ModelFormat {
    // Strip query string and fragment before extracting extension
    const clean = url.split('?')[0].split('#')[0];
    const dotIndex = clean.lastIndexOf('.');
    if (dotIndex === -1) {
      throw new Error(
        `Unsupported model format: no extension found. Supported: ${SUPPORTED_EXTENSIONS}`,
      );
    }

    const ext = clean.slice(dotIndex).toLowerCase();
    const format = EXTENSION_MAP[ext];
    if (!format) {
      throw new Error(
        `Unsupported model format: "${ext}". Supported: ${SUPPORTED_EXTENSIONS}`,
      );
    }

    return format;
  }
}
